import type {
  ThemeConfig,
  ThemeCustomTokenValues,
  ThemeToken,
} from "./theme.type";

import { omitKeysBy } from "@/utilities/object";
import { toKebabCase } from "@/utilities/string";

import { ANT_DESIGN_TOKEN_KEYS_TO_REMOVE } from "./theme.constant";

export const getAppThemeFromToken = (token: ThemeToken) => {
  const { customToken, customColors, ...tokenData } = token;

  // Keep only AntD variables needed (aka delete useless colors)
  const cleanToken = omitKeysBy(tokenData, (key: string) => {
    const shortKey = key.replace(/[0-9]/g, "").split("-")[0];
    return !ANT_DESIGN_TOKEN_KEYS_TO_REMOVE.includes(shortKey);
  });

  return {
    colors: customColors,
    token: {
      // Return Stelar custom variables & AntD base variables at the same level
      ...customToken,
      ...cleanToken,
    },
  };
};

const getCssVariables = (
  variablesObject: ThemeCustomTokenValues,
  prefix: string
) => {
  return Object.entries(variablesObject).map(([variableKey, variableValue]) => {
    const variableName = `--${prefix}-${toKebabCase(variableKey, true)}`;
    return `${variableName}:${variableValue}`;
  });
};

const getThemeConfigCssVarInfos = (theme: ThemeConfig) => {
  if (typeof theme.cssVar === "boolean") return {};

  return theme.cssVar ?? {};
};

export const generateCustomCssVariables = (theme: ThemeConfig) => {
  const { prefix, key } = getThemeConfigCssVarInfos(theme);
  const colorPrefix = `${prefix}-color`;

  const colorsVars = getCssVariables(theme.token.customColors, colorPrefix);
  const tokenVars = getCssVariables(theme.token.customToken, prefix ?? "");

  const variables = [...colorsVars, ...tokenVars].join(";");

  return `.${key} { ${variables}; }`;
};
